import { Button } from '@/components/ui/Button'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/utils'
import type { PendingAction } from '@/types/api'

const MAX_ITEMS = 10

/**
 * Something the assistant proposes to do. Nothing runs until the user
 * confirms; afterwards the card shows how it went.
 */
export function ActionCard({
  action,
  busy,
  disabled,
  onConfirm,
  onDismiss,
}: {
  action: PendingAction
  /** This action's request is in flight. */
  busy: boolean
  /** Another action is being confirmed. */
  disabled: boolean
  onConfirm: () => void
  onDismiss: () => void
}) {
  const items = action.items ?? []
  const shown = items.slice(0, MAX_ITEMS)
  const more = items.length - shown.length
  const pending = action.status === 'pending'

  return (
    <div className={cn('panel mt-3 overflow-hidden', !pending && 'bg-surface-muted/60')} data-testid="action-card">
      <div className="px-3.5 pt-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[14px] font-semibold leading-snug text-ink">{action.title}</p>
          {action.status === 'done' && <StatusPill tone="success">Done</StatusPill>}
          {action.status === 'failed' && <StatusPill tone="danger">Failed</StatusPill>}
          {action.status === 'dismissed' && <StatusPill tone="neutral">Not now</StatusPill>}
        </div>
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{action.summary}</p>
      </div>

      {shown.length > 0 && (
        <ul className="mt-2.5 divide-y divide-line border-t border-line">
          {shown.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-3.5 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-medium text-ink">{item.label}</span>
                {item.sublabel && <span className="block truncate text-[12px] text-ink-muted">{item.sublabel}</span>}
              </span>
              {typeof item.score === 'number' && (
                <span className="num shrink-0 text-[12.5px] font-medium text-brand" title="Match score">
                  {Math.round(item.score)}
                </span>
              )}
            </li>
          ))}
          {more > 0 && <li className="px-3.5 py-2 text-[12.5px] text-ink-muted">+{more} more</li>}
        </ul>
      )}

      {pending ? (
        <div className="flex gap-2 border-t border-line p-2.5">
          <Button size="md" className="flex-1" loading={busy} disabled={disabled} onClick={onConfirm}>
            Confirm
          </Button>
          <Button size="md" variant="ghost" disabled={busy || disabled} onClick={onDismiss}>
            Not now
          </Button>
        </div>
      ) : (
        action.result && (
          <p className={cn('border-t border-line px-3.5 py-2.5 text-[13px] leading-relaxed', action.status === 'failed' ? 'text-danger' : 'text-ink-soft')}>
            {action.result}
          </p>
        )
      )}
    </div>
  )
}
